import {Injectable} from '@angular/core';

import {catchError, interval, map, Observable, tap, throwError} from 'rxjs';
import {BehaviorSubject} from "rxjs/internal/BehaviorSubject";

import {differenceInMinutes, isAfter, parseISO} from 'date-fns'

import {AuthenticateUserRequest} from '@commons/models/remote.request/AuthenticateUserRequest';
import {RememberMeToken} from '@commons/models/domain/RememberMeToken';
import {AuthenticateUserResponse} from "@commons/models/remote.response/AuthenticateUserResponse";

import {NewNavigationService} from '@shared/services/new-navigation.service';
import {PREVIOUS_SELECTED_DATABASE_LSKEY, SettingsService} from "@shared/services/settings.service";
import {AuthenticationHttpService} from "@shared/services-http/authentication-http.service";
import {TokenStore} from "@shared/services/authentication/token.store";
import {RememberMeTokenStore} from "@shared/services/authentication/remember-me-token.store";
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {

  private readonly isAuthenticatedBS = new BehaviorSubject(false);

  get isAuthenticated$(): Observable<boolean> {
    return this.isAuthenticatedBS;
  }

  private readonly tokenStore = new TokenStore();

  private readonly rememberMeTokenStore = new RememberMeTokenStore();

  get canSelectDatabase(): boolean {
    const clients = this.tokenStore?.token?.clients;
    const count = clients?.length ?? 0;
    const isMultiDatabase = count > 1;

    return isMultiDatabase;
  }

  get currentClient(): string {
    return this.tokenStore.token?.clients ? this.tokenStore.token?.clients[0] : '';
  }

  get isAuthenticatedWithoutDatabase(): boolean {
    let result = false;
    const token = this.tokenStore?.token;
    if (token?.validTo) {
      const now = new Date();
      const validTo = parseISO(token?.validTo);
      const isActive = token?.sessionId != null && isAfter(validTo, now);
      const hasManyDatabases = token?.clients != null && token?.clients?.length !== 1;
      result = isActive && hasManyDatabases;
    }
    return result;
  }

  get isSessionActive(): boolean {
    let result = false;
    const token = this.tokenStore?.token;
    if (token?.validTo) {
      const now = new Date();
      const validTo = parseISO(token.validTo);
      result = token.sessionId != null && isAfter(validTo, now);
      if (!result) {
        this.tokenStore.removeToken();
      }
    }
    return result && token?.clients?.length === 1; //pełne zalogowanie jest wtedy gdy klient został już wybrany
  }

  get isTekomUser(): boolean {
    const email = this.settingsService.getLoggedUserEmail();
    const result = email ? email.includes('@tekom.pl') : false;
    return result;
  }

  get isLoggedByToken(): boolean {
    return this._isLoggedByToken;
  }

  get sessionId(): string | undefined {
    return this.tokenStore?.token?.sessionId;
  }

  private _isLoggedByToken = false;
  private firstStepRequest?: AuthenticateUserRequest;

  constructor(
    private newNavigationService: NewNavigationService,
    private settingsService: SettingsService,
    private authenticationHttpService: AuthenticationHttpService,
    private router: Router,
  ) {
    this.subToIsAuthenticatedBS();
    if (!this.newNavigationService.isShareLink && !this.newNavigationService.isHandoverView && this.tokenStore.token && this.isSessionActive) {
      console.log('[auth.service] constructor isAuthenticatedBS = true');

      this.isAuthenticatedBS.next(true);
    }
  }

  hasModule(module: string): boolean {
    if (this.tokenStore?.token?.modules == null) {
      return false;
    }

    return this.tokenStore.token.modules.includes(module);
  }

  hasUserRight(userRight: string): boolean {
    if (this.tokenStore?.token?.userRights == null) {
      return false;
    }
    return this.tokenStore.token!.userRights.includes(userRight);
  }

  getDatabases(): string[] {
    return (this.tokenStore.token?.clients || []).sort((a, b) => a.localeCompare(b));
  }

  authenticateWithDb(databaseName: string): Observable<AuthenticateUserResponse> {
    const request: AuthenticateUserRequest = {} as AuthenticateUserRequest;
    request.Login = this.firstStepRequest!.Login;
    request.Password = this.firstStepRequest!.Password;
    request.RememberMeEnabled = this.firstStepRequest!.RememberMeEnabled;
    request.SessionId = this.tokenStore.token?.sessionId;
    request.DB = databaseName;

    return this.authenticate(request);
  }

  startKeepSessionInterval() {
    const loginByRememberToken = (): void => {
      if (this.rememberMeTokenStore.token?.value) {
        this.tokenStore.removeToken();
        document.location.reload();
        return;
      }
      this.newNavigationService.toLogout();
    };

    //na produkcyjnym API czas trwania sesji wynosi 20 min, na test2 60 min
    interval(1000).subscribe(() => {
        if (this.isSessionActive) {

          const validTo = parseISO(this.tokenStore.token!.validTo);
          const timeLeft = differenceInMinutes(validTo, new Date());

          if (timeLeft <= 2)
            this.authenticationHttpService.keepSession().subscribe(response => {
              if (response.Status === 200) {
                this.tokenStore.extendTokenValidTo();
                return;
              }
              loginByRememberToken();
            });
          return;
        }
        loginByRememberToken();
      }
    );
  }

  authenticate(request: AuthenticateUserRequest): Observable<AuthenticateUserResponse> {
    return this.authenticationHttpService.authenticateUser(request).pipe(
      tap(response => {
        this.settingsService.setSkipChooseLoginMethod();
        this.tokenStore.saveToken(response, true);
        const rememberMeToken = response?.rememberMeToken ? new RememberMeToken(response?.rememberMeToken, request.DB) : undefined;
        if (rememberMeToken)
          this.rememberMeTokenStore.save(rememberMeToken);

        const isClientSelected = response.clients?.length === 1;
        if (isClientSelected && this.firstStepRequest) {
          this.isAuthenticatedBS.next(isClientSelected);
          const [clientDatabase] = response.clients!;
          this.settingsService.setPreviouslySelectedDatabase(clientDatabase);
        }
        this.firstStepRequest = request;
      }
    ));
  }

  authenticateByToken(token: string): Observable<AuthenticateUserResponse> {
    return this.authenticationHttpService.authenticateByToken(token).pipe(
      tap(response => {
        this._isLoggedByToken = true;
        this.tokenStore.saveToken(response, false);
        this.isAuthenticatedBS.next(true);
      }));
  }

  closeSession(): Observable<boolean> {
    const sessionId = this.tokenStore.token?.sessionId ?? '';
    if (!this._isLoggedByToken) {
      this.tokenStore.removeToken();
      this.rememberMeTokenStore.clear();
      localStorage.removeItem(PREVIOUS_SELECTED_DATABASE_LSKEY);
    }

    return this.authenticationHttpService.closeUserSession(sessionId)
      .pipe(map(response => response.ErrorCode === 0));
  }

  public authenticateWithRememberMeToken(): Observable<AuthenticateUserResponse> {
    if (!this.rememberMeTokenStore.token) {
      return new Observable<AuthenticateUserResponse>(observer => {
        observer.error();
        observer.complete();
      });
    }

    const request = {} as AuthenticateUserRequest;
    request.DB = this.settingsService.getPreviouslySelectedDatabase() || '';
    request.RememberMeToken = this.rememberMeTokenStore.token?.value;

    return this.authenticate(request);
  }

  private subToIsAuthenticatedBS() {
    this.isAuthenticatedBS.subscribe(isActive => {
      this.processIsAuthenticated(isActive);
    });
  }

  private processIsAuthenticated(isAuthenticated: boolean): void {
    const shareLinkIdValue = this.getShareLinkIdValueFromWindowURL();
    if (shareLinkIdValue) {
      this.newNavigationService.toShare(shareLinkIdValue);
      return;
    }

    const ifHandoverView = this.getWindowPathName().includes('handover-view');
    if (ifHandoverView) {
      const [,, handoverToken] = this.getWindowPathName().split('/');
      this.newNavigationService.toHandoverAuthView(handoverToken);
      return;
    }

    if (isAuthenticated) {
      this.router.navigate(['/panel', 'main']); // to main panel - serwis newNawigation nie działa w tym miejscu przy wygasnietym tokenie, widocznie jakims problem z inicjalizacja serwisow
    }
    else if (this.rememberMeTokenStore?.token) {
      setTimeout(() => {
        this.authenticateWithRememberMeToken().pipe(
          catchError((err) => {
            this.newNavigationService.toLogout();
            return throwError(() => err);
          })
        )
        .subscribe(() => {
          this.isAuthenticatedBS.next(true);
        });
      }, 300);
    }
    else if (
      !this.newNavigationService.isInAuthenticationModule &&
      !this.newNavigationService.isShareLink &&
      !this.newNavigationService.isHandoverView &&
      !this.newNavigationService.isPasswordResetConfirmView &&
      !this.newNavigationService.isPasswordResetView
    ) {
      this.newNavigationService.toLogin();
    }
  }

  private getShareLinkIdValueFromWindowURL(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    const shareLinkIdValue = urlParams.get('shareLinkId');
    return shareLinkIdValue;
  }

  private getWindowPathName(): string {
    return window.location.pathname;
  }

}
